/**
 * v0.4 追加要件⑩ (D-325/D-326): 恒常パターン調整画面 (ShiftStandingPatternsPage.tsx) の表示行を
 * 「ロースター (people) + 店長が応援候補から追加した人物」に合成するロジック。
 * ページ内 useMemo に留めず抽出する (4並列レビュー test-runner 指摘反映: `shift-board-filters.ts`
 * と同じ理由 — ページ内ロジックのまま放置すると変更が無テストで壊れる)。
 */

export type Entry = {
  availability_id: string; day_of_week: number; start_time: string; end_time: string;
  crosses_midnight: boolean; preference_level_id: string; shift_pattern_id: string | null; source: string;
  // v0.4 追加要件⑫ 増分③ (D-379): この枠がテンプレートから展開されたものならそのID
  template_id: string | null;
  // §8-221対策: 廃止済み(DELETE)の選好レベルでも解決できるよう、サーバーが埋め込んだ値
  // (routes/standing-patterns.ts serializeEntry 参照)。現行マスタ一覧 (`/preference-levels`、
  // 廃止済みは含まれない) からの逆引きに頼ると生ID表示になるため、表示は必ずこちらを優先する。
  preference_level_label: string | null;
  preference_level_is_available: boolean | null;
};

export type Person = {
  employee_id: string | null; external_person_id: string | null; display_name: string; entries: Entry[];
  // v0.4 追加要件⑫ 増分② (D-380): 表示専用の派生指標。null は「週所定が未設定」を表す (§6-0 原則3)。
  weekly_contracted_work_days: { value: number; source_kind: string } | null;
  coverage: number | null;
  flex_slots: number | null;
};

export type SupportCandidate = { employee_id: string; display_name: string; home_node_id: string | null };

/** ロースター (people) に、応援候補のうち「追加済み」の人物を合成し、氏名順に並べる。
 *  追加直後の応援候補は entries が空 (週所定・充足はまだ確定していないため null、§6-0 原則3)。
 *  `addedSupportEmployeeIds` が空 (discard 直後・nodeId 変更直後) なら `people` のみを返す
 *  (呼び出し側が discard 時にこの Set を空へ戻すことで、追加済みの空行を確実に取り消せる)。 */
export const buildDisplayPeople = (
  people: readonly Person[],
  supportCandidates: readonly SupportCandidate[],
  addedSupportEmployeeIds: ReadonlySet<string>,
): Person[] => {
  const extra: Person[] = supportCandidates
    .filter((sc) => addedSupportEmployeeIds.has(sc.employee_id))
    .map((sc) => ({
      employee_id: sc.employee_id, external_person_id: null, display_name: sc.display_name,
      entries: [], weekly_contracted_work_days: null, coverage: null, flex_slots: null,
    }));
  return [...people, ...extra].sort((a, b) => a.display_name.localeCompare(b.display_name, 'ja'));
};
